/**
 * localPrintService — local-service yazdırma çağrıları için ortak helper (Faz C).
 *
 * Yeni local-service (v1.7+, Faz B) `async: true` isteğine anında `202 { jobId }`
 * döner; sonuç `GET /api/print-job/:jobId` poll edilerek izlenir. Böylece:
 *  - HTTP cevabı kuyruğun arkasında bekletilmez (timeout → "hata gösterildi ama
 *    fiş yine basıldı" çift basım algısı biter),
 *  - `PRINTER_OFFLINE` gibi gerçek hatalar Türkçe mesajla kullanıcıya taşınır.
 *
 * ESKİ local-service `async` alanını tanımaz ve senkron `200` döner — bu durumda
 * cevap aynen kullanılır. Sürüm uyumsuzluğu hiçbir akışı bozmaz.
 */
import axios, { AxiosError } from 'axios';

export interface PrintReceiptPayload {
  printerId?: string | number;
  printerName: string;
  receiptData: any;
}

export interface PrintReceiptOptions {
  /** Toplam bütçe: POST + poll (varsayılan 30 sn). */
  timeoutMs?: number;
  /** local-service kök adresi (varsayılan http://localhost:3003). */
  baseUrl?: string;
  /** Yazdırma endpoint yolu (qr-menu '/print-receipt' kullanır; varsayılan '/api/print-receipt'). */
  path?: string;
}

export interface PrintReceiptResult {
  message: string;
  jobId?: string;
  deduped?: boolean;
  copies?: number;
  duration?: number;
  /** Poll sırasında iş kaydı bulunamadıysa true — fiş büyük olasılıkla basıldı. */
  unverified?: boolean;
}

export class LocalPrintError extends Error {
  errorCode: string;
  constructor(message: string, errorCode: string) {
    super(message);
    this.name = 'LocalPrintError';
    this.errorCode = errorCode;
  }
}

const DEFAULT_BASE_URL = 'http://localhost:3003';
const POLL_INTERVAL_MS = 500;
const POLL_REQUEST_TIMEOUT_MS = 5000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Her printReceipt çağrısı için benzersiz kimlik. local-service (v1.7.1+) dedup'u
 * bu kimliğe göre yapar: yalnızca AYNI isteğin tekrarı engellenir; aynı içerikli
 * ama ayrı çağrılar (örn. ilave fişinin hemen ardından aynı ürünün iptal fişi)
 * asla "çift basım" sanılıp yutulmaz.
 */
function newClientRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) { /* eski tarayıcı — aşağıdaki fallback */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function mapErrorMessage(errorCode: string | undefined, serverMessage: string | undefined): string {
  switch (errorCode) {
    case 'PRINTER_OFFLINE':
      return 'Yazıcı kapalı veya bağlantısı yok. Yazıcının açık ve bağlı olduğundan emin olun.';
    case 'TIMEOUT':
      return 'Yazdırma zaman aşımına uğradı. Yazıcı bağlantısını kontrol edin.';
    case 'QUEUE_FULL':
      return 'Yazdırma kuyruğu dolu. Lütfen birkaç saniye sonra tekrar deneyin.';
    default:
      return serverMessage || 'Yazdırma sırasında bir hata oluştu.';
  }
}

async function pollJob(baseUrl: string, jobId: string, deadlineTs: number): Promise<PrintReceiptResult> {
  while (Date.now() < deadlineTs) {
    await sleep(POLL_INTERVAL_MS);
    let jobData: any;
    try {
      const resp = await axios.get(`${baseUrl}/api/print-job/${jobId}`, { timeout: POLL_REQUEST_TIMEOUT_MS });
      jobData = resp.data;
    } catch (err) {
      const ax = err as AxiosError;
      if (ax.response?.status === 404) {
        // Kayıt süresi dolmuş — fiş büyük olasılıkla basıldı; kullanıcıyı yanlış hata ile korkutma.
        return { message: 'Yazdırma sonucu doğrulanamadı (iş kaydı bulunamadı)', jobId, unverified: true };
      }
      continue; // geçici ağ hatası — poll devam eder, toplam bütçe deadlineTs ile sınırlı
    }
    if (jobData?.status === 'done') {
      const result = jobData.result || {};
      return {
        message: result.message || 'Fiş yazdırıldı.',
        copies: result.copies,
        duration: result.duration,
        jobId,
      };
    }
    if (jobData?.status === 'failed') {
      throw new LocalPrintError(
        mapErrorMessage(jobData.errorCode, jobData.error),
        jobData.errorCode || 'PRINT_ERROR'
      );
    }
    // queued | printing → beklemeye devam
  }
  throw new LocalPrintError('Yazdırma zaman aşımına uğradı (sonuç alınamadı).', 'TIMEOUT');
}

/**
 * Fiş yazdırma isteği gönderir; yeni local-service'te jobId poll eder,
 * eski local-service'te senkron cevabı aynen kullanır.
 *
 * Başarıda `PrintReceiptResult` döner; hatada Türkçe mesajlı `LocalPrintError` fırlatır
 * (`error.message` doğrudan kullanıcıya gösterilebilir, `error.errorCode` programatik ayrım için).
 */
export async function printReceipt(
  payload: PrintReceiptPayload,
  options: PrintReceiptOptions = {}
): Promise<PrintReceiptResult> {
  const { timeoutMs = 30000, baseUrl = DEFAULT_BASE_URL, path = '/api/print-receipt' } = options;
  const deadlineTs = Date.now() + timeoutMs;

  let postResp;
  try {
    postResp = await axios.post(
      `${baseUrl}${path}`,
      { ...payload, async: true, clientRequestId: newClientRequestId() },
      { timeout: timeoutMs }
    );
  } catch (err) {
    const ax = err as AxiosError<any>;
    if (ax.response) {
      const data = ax.response.data || {};
      if (ax.response.status === 429) {
        throw new LocalPrintError(mapErrorMessage('QUEUE_FULL', data.error), 'QUEUE_FULL');
      }
      // 400/500: yeni serviste errorCode gelir, eskisinde yalnız error/details
      throw new LocalPrintError(
        mapErrorMessage(data.errorCode, data.details || data.error),
        data.errorCode || 'PRINT_ERROR'
      );
    }
    if (ax.code === 'ECONNABORTED') {
      throw new LocalPrintError('Yazdırma zaman aşımına uğradı (yerel servis yanıt vermedi).', 'TIMEOUT');
    }
    throw new LocalPrintError(
      'Yerel servise ulaşılamadı. AQUASOFT uygulamasının açık olduğundan emin olun.',
      'SERVICE_UNREACHABLE'
    );
  }

  // Yeni servis, async mod: 202 + jobId → sonucu poll et
  if (postResp.status === 202 && postResp.data?.jobId) {
    return pollJob(baseUrl, postResp.data.jobId, deadlineTs);
  }

  // Eski servis (senkron 200) veya dedup cevabı → aynen kullan
  const data = postResp.data || {};
  return {
    message: data.message || 'Fiş yazdırıldı.',
    copies: data.copies,
    duration: data.duration,
    deduped: data.deduped,
    jobId: data.jobId,
  };
}
